// SessionTabStrip.js — Fixed bottom bar showing all active sessions.
// Mounts ONCE in App.js so it survives every route change and page-nav.
//
// Source of truth strategy:
//   • SessionManagerContext — primary source, kept in sync via Socket.io events
//     across all devices. On connect, the backend emits session:init with all
//     live sessions so a page refresh immediately repopulates the context.
//   • /appointments/live — fallback poll on mount only to catch any sessions
//     the socket may have missed (e.g. if socket wasn't connected during start).
//   • Timer shown = wall-clock elapsed from appointment.actualStartTime for
//     sessions where context lacks elapsed data; context elapsed otherwise.
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from './api';
import { useSessionManager } from '../context/SessionManagerContext';
import AuthContext from '../context/AuthProvider';

// ── Constants ─────────────────────────────────────────────────────────────────
export const STRIP_HEIGHT = 68; // px — MainLayout adds this as paddingBottom

// Fallback poll interval — only used on mount to catch sessions started before
// this device connected to the socket, and to hydrate after a hard refresh.
const FALLBACK_POLL_MS = 60_000; // 60 s — much less frequent than before

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTimer(sec) {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
}

function sessionInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '?';
}

function sessionAvatarColor(name = '') {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#0ea5e9','#10b981','#14b8a6'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[h];
}

function elapsedSec(startStr) {
  if (!startStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startStr).getTime()) / 1000));
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SessionTabStrip() {
  const navigate = useNavigate();
  const location = useLocation();
  const sm       = useSessionManager();

  // Auth context — subscribed for re-renders when user logs in.
  // token/lu from sessionStorage only — avoids stale localStorage tokens triggering 401s.
  const { auth } = useContext(AuthContext);
  const token     = sessionStorage.getItem('token');
  const lu        = sessionStorage.getItem('lu');
  const isSaadhak = (auth?.roles || []).includes('saadhak');

  const [liveAppts, setLiveAppts] = useState([]); // fallback from /appointments/live
  const [, setTick] = useState(0);                // 1-sec heartbeat → re-renders timers

  const pollRef = useRef(null);
  const tickRef = useRef(null);

  // ── Fallback poll /appointments/live (infrequent) ─────────────────────────
  // Primary data now comes through SessionManagerContext (populated via socket).
  // This poll catches any sessions that were started before this device connected.
  const fetchLive = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/appointments/live');
      setLiveAppts(res.data || []);
    } catch (e) {
      // silently ignore
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token || isSaadhak) return;
    fetchLive();                                                   // once on mount
    pollRef.current = setInterval(fetchLive, FALLBACK_POLL_MS);   // slow fallback
    tickRef.current = setInterval(() => setTick(t => t + 1), 1000); // timer redraw
    return () => {
      clearInterval(pollRef.current);
      clearInterval(tickRef.current);
    };
  }, [token, isSaadhak]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build unified tab list ────────────────────────────────────────────────
  // Backend is the authority for WHICH sessions exist.
  // Context enriches with exercise data + pause/ding state for this device.
  const contextSessions = sm?.sessions || {}; // { [patientCard]: sessionObj }

  // liveAppts filtered to this therapist only
  const myLiveAppts = liveAppts.filter(a =>
    a.patientCard &&
    a.username?.toLowerCase() === (lu || '').toLowerCase()
  );

  const allTabs = myLiveAppts.map(a => {
    const ctx       = contextSessions[a.patientCard]; // may be undefined (other device / post-refresh)
    const name      = a.profileName || a.name || a.patientCard || 'Unknown';
    const startedAt = a.actualStartTime || a.start;   // used for the wall-clock timer
    return {
      patientCard: a.patientCard,
      patientName: ctx?.patientName || name,
      startedAt,                                       // wall-clock session start
      isPaused:    ctx?.isPaused    ?? false,
      isDinging:   ctx?.isDinging   ?? false,
      finishedAt:  ctx?.finishedAt  ?? null,           // all exercises done, awaiting completion
      exercises:   ctx?.exercises   || [],
      activeIdx:   ctx?.activeIdx   ?? 0,
      repElapsed:  ctx?.elapsedSec  ?? null,           // per-rep timer from context (null if no ctx)
      hasCtx:      !!ctx,                              // true → exercise data available
      rawAppt:     a,                                  // full appointment object for navigation
    };
  });

  // Also include context sessions not yet returned by the backend poll
  // (edge case: just started, poll hasn't refreshed yet)
  const backendCards = new Set(myLiveAppts.map(a => a.patientCard));
  Object.values(contextSessions).forEach(s => {
    if (!backendCards.has(s.patientCard)) {
      const startedAt = s.appointment?.actualStartTime || s.appointment?.start;
      allTabs.push({
        patientCard: s.patientCard,
        patientName: s.patientName,
        startedAt,
        isPaused:    s.isPaused,
        isDinging:   s.isDinging,
        finishedAt:  s.finishedAt ?? null,
        exercises:   s.exercises || [],
        activeIdx:   s.activeIdx ?? 0,
        repElapsed:  s.elapsedSec ?? null,
        hasCtx:      true,
        rawAppt:     s.appointment || null,
      });
    }
  });

  // Don't render if no sessions or not authenticated
  if (!token || isSaadhak || allTabs.length === 0) return null;

  // ── Highlight the tab for the currently-open session ──────────────────────
  const viewingCard = location.pathname.startsWith('/SessionIpadView/')
    ? decodeURIComponent(location.pathname.slice('/SessionIpadView/'.length).split('?')[0])
    : null;

  const goToSession = (patientCard, rawAppt) => {
    sm?.acknowledgeCard?.(patientCard);
    navigate(`/SessionIpadView/${encodeURIComponent(patientCard)}`, {
      state: rawAppt ? { resumeAppt: rawAppt } : undefined,
    });
  };

  return (
    <>
      <style>{`
        @keyframes stsDing {
          0%,100% { background: rgba(239,68,68,0.18); }
          50%      { background: rgba(239,68,68,0.06); }
        }
        @keyframes stsLivePulse {
          0%,100% { box-shadow: 0 0 4px #22c55e; }
          50%      { box-shadow: 0 0 10px #22c55e; }
        }
        .stsRow::-webkit-scrollbar { display: none; }
      `}</style>

      <div
        className="stsRow"
        style={{
          position:        'fixed',
          bottom:          0,
          left:            0,
          right:           0,
          height:          STRIP_HEIGHT,
          zIndex:          5000,
          display:         'flex',
          overflowX:       'auto',
          overflowY:       'hidden',
          background:      'linear-gradient(180deg,#0b1828 0%,#060e1c 100%)',
          borderTop:       '1px solid rgba(255,255,255,0.10)',
          boxShadow:       '0 -4px 24px rgba(0,0,0,0.60)',
          msOverflowStyle: 'none',
          scrollbarWidth:  'none',
        }}
      >
        {/* ── "SESSIONS N" label ── */}
        <div style={{
          display:     'flex',
          flexDirection:'column',
          alignItems:  'center',
          justifyContent:'center',
          padding:     '0 12px 0 14px',
          flexShrink:  0,
          borderRight: '1px solid rgba(255,255,255,0.07)',
          gap:         3,
          minWidth:    60,
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#22c55e',
            display: 'inline-block',
            animation: 'stsLivePulse 2s ease-in-out infinite',
          }} />
          <span style={{
            fontSize: 9, fontWeight: 800, color: '#334155',
            letterSpacing: 0.8, whiteSpace: 'nowrap',
          }}>
            {allTabs.length} LIVE
          </span>
        </div>

        {/* ── Session tabs ── */}
        {allTabs.map(s => {
          const isActive    = s.patientCard === viewingCard;
          const isDinging   = s.isDinging;
          const isFinished  = !!s.finishedAt && !isDinging; // all exercises done
          const bgColor     = sessionAvatarColor(s.patientName);
          const ex        = s.exercises?.[s.activeIdx];
          const done      = s.exercises.filter(e => e.status === 'Completed').length;
          const total     = s.exercises.length;
          const elapsed   = elapsedSec(s.startedAt); // wall-clock, same on all devices

          return (
            <div
              key={s.patientCard}
              onClick={() => goToSession(s.patientCard, s.rawAppt)}
              style={{
                display:        'flex',
                flexDirection:  'column',
                justifyContent: 'center',
                padding:        '7px 14px 7px 10px',
                minWidth:       210,
                maxWidth:       270,
                flexShrink:     0,
                cursor:         'pointer',
                userSelect:     'none',
                borderRight:    '1px solid rgba(255,255,255,0.06)',
                borderTop:      isActive
                  ? '2.5px solid #3b82f6'
                  : isDinging
                    ? '2.5px solid #ef4444'
                    : isFinished
                      ? '2.5px solid #facc15'
                      : '2.5px solid transparent',
                background: isActive
                  ? 'rgba(59,130,246,0.14)'
                  : isDinging
                    ? 'rgba(239,68,68,0.12)'
                    : isFinished
                      ? 'rgba(250,204,21,0.10)'
                      : 'transparent',
                transition:  'background 0.15s',
                animation:   isDinging ? 'stsDing 0.6s ease-in-out infinite' : 'none',
              }}
              onMouseEnter={e => {
                if (!isActive && !isDinging)
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }}
              onMouseLeave={e => {
                if (!isActive && !isDinging)
                  e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Row 1: avatar · patient name · timer */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>

                {/* Initials avatar + status dot */}
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: `linear-gradient(135deg,${bgColor}dd,${bgColor}88)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 800, fontSize: 9.5,
                  flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.18)',
                  position: 'relative',
                }}>
                  {sessionInitials(s.patientName)}
                  <span style={{
                    position: 'absolute', bottom: 0, right: 0,
                    width: 8, height: 8, borderRadius: '50%',
                    border: '1.5px solid #060e1c',
                    background: isDinging ? '#ef4444' : isFinished ? '#facc15' : s.isPaused ? '#f59e0b' : '#22c55e',
                    boxShadow: isDinging || s.isPaused ? 'none' : '0 0 4px #22c55e',
                  }} />
                </div>

                {/* Name */}
                <span style={{
                  color:        isDinging ? '#fca5a5' : isFinished ? '#fef08a' : isActive ? '#f1f5f9' : '#cbd5e1',
                  fontWeight:   700,
                  fontSize:     12.5,
                  flex:         1,
                  overflow:     'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace:   'nowrap',
                }}>
                  {s.patientName}
                </span>

                {/* Timers: rep (context, live) on top; total wall-clock below */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: 1 }}>
                  {/* Rep / exercise timer — from SessionManagerContext, ticks in background */}
                  {s.repElapsed !== null && (
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize:   12,
                      fontWeight: 700,
                      color:      isDinging ? '#ef4444' : isFinished ? '#facc15' : s.isPaused ? '#f59e0b' : '#4ade80',
                      textShadow: isDinging || s.isPaused || isFinished ? 'none' : '0 0 6px rgba(74,222,128,0.4)',
                    }}>
                      {fmtTimer(s.repElapsed)}
                    </span>
                  )}
                  {/* Total session timer — wall-clock, consistent across devices + refreshes */}
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize:   s.repElapsed !== null ? 9 : 12,
                    fontWeight: s.repElapsed !== null ? 500 : 700,
                    color:      s.repElapsed !== null ? '#334155' : (isDinging ? '#ef4444' : isFinished ? '#facc15' : s.isPaused ? '#f59e0b' : '#4ade80'),
                    textShadow: 'none',
                  }}>
                    {s.repElapsed !== null ? `${fmtTimer(elapsed)} tot` : fmtTimer(elapsed)}
                  </span>
                </div>
              </div>

              {/* Row 2: status label · exercise progress */}
              <div style={{
                display:     'flex',
                alignItems:  'center',
                gap:         4,
                marginTop:   4,
                paddingLeft: 33,
              }}>
                <span style={{
                  fontSize:   9.5,
                  fontWeight: 700,
                  color:      isDinging ? '#ef4444' : isFinished ? '#facc15' : s.isPaused ? '#f59e0b' : '#22c55e',
                  flexShrink: 0,
                }}>
                  {isDinging ? '⏰ DING' : isFinished ? '✅ Close Session' : s.isPaused ? '⏸ Paused' : '● Live'}
                </span>

                {total > 0 && (
                  <span style={{ fontSize: 9.5, color: '#475569', flexShrink: 0 }}>
                    · {done}/{total}
                  </span>
                )}

                {ex?.name && (
                  <span style={{
                    fontSize:     9.5,
                    color:        '#475569',
                    overflow:     'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace:   'nowrap',
                    flex:         1,
                  }}>
                    · {ex.name}
                  </span>
                )}

                {total > 0 && (
                  <div style={{
                    width: 32, height: 3, borderRadius: 2,
                    background: 'rgba(255,255,255,0.07)',
                    overflow: 'hidden', flexShrink: 0,
                  }}>
                    <div style={{
                      height:     '100%',
                      width:      `${(done / total) * 100}%`,
                      background: isDinging ? '#ef4444' : '#22c55e',
                      borderRadius: 2,
                      transition: 'width 0.4s',
                    }} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
